import { ScramjetClient } from "@client/index";
import {
	Number,
	Object_getOwnPropertyDescriptor,
	Reflect_apply,
	Reflect_get,
	Reflect_has,
	Reflect_ownKeys,
} from "@/shared/snapshot";

export default function (client: ScramjetClient, self: Self) {
	// element.attributes without Scramjet's own scramjet-attr-* attributes. The visible ones are
	// numbered 0 to length - 1 without gaps, as in a browser: before, a hidden attribute left a
	// hole that Array.prototype.slice.call(attributes) turned into undefined (MyFreeCams).
	const hidden = (name: unknown) =>
		typeof name === "string" && name.startsWith("scramjet-attr-");
	const isIndex = (prop: string | symbol) =>
		typeof prop === "string" && /^(0|[1-9][0-9]*)$/.test(prop);

	client.Trap("Element.prototype.attributes", {
		get(ctx) {
			const map = ctx.get() as NamedNodeMap;
			const element = ctx.this as Element;
			const visible = () => {
				const out: Attr[] = [];
				for (let i = 0; i < map.length; i++) {
					const attr = map[i];
					if (!hidden(attr.name)) out.push(attr);
				}

				return out;
			};
			const proxy = new Proxy(map, {
				get(target, prop) {
					if (prop === "length") return visible().length;
					if (isIndex(prop)) return visible()[Number(prop)];
					if (prop === Symbol.iterator) return () => visible()[Symbol.iterator]();
					if (prop === "item") return (index: number) => visible()[index] ?? null;
					if (prop === "getNamedItem") {
						return (name: string) => (hidden(name) ? null : map.getNamedItem(name));
					}
					// An Attr put in through the map is rewritten like setAttributeNode (element.ts);
					// natively it went in as written, and an image loaded straight from the site.
					if (prop === "setNamedItem" || prop === "setNamedItemNS") {
						return (attr: Attr) =>
							Reflect_apply(self.Element.prototype.setAttributeNode, element, [attr]);
					}
					if (prop === "getNamedItemNS") {
						return (namespace: string, name: string) =>
							hidden(name) ? null : map.getNamedItemNS(namespace, name);
					}

					const value = Reflect_get(target, prop);
					if (prop in NamedNodeMap.prototype && typeof value === "function") {
						return new Proxy(value, {
							apply(target, that, args) {
								return Reflect_apply(target, that === proxy ? map : that, args);
							},
						});
					}
					if (hidden(prop)) return undefined;

					return value;
				},
				has(target, prop) {
					if (isIndex(prop)) return Number(prop) < visible().length;
					if (hidden(prop)) return false;

					return Reflect_has(target, prop);
				},
				ownKeys(target) {
					const indices = visible().map((_, i) => String(i));
					const others = Reflect_ownKeys(target).filter(
						(key) => !isIndex(key) && !hidden(key)
					);

					return [...indices, ...others];
				},
				getOwnPropertyDescriptor(target, prop) {
					if (isIndex(prop)) {
						const attr = visible()[Number(prop)];

						return attr
							? { value: attr, writable: false, enumerable: true, configurable: true }
							: undefined;
					}
					if (hidden(prop)) return undefined;

					return Object_getOwnPropertyDescriptor(target, prop);
				},
			});

			return proxy;
		},
	});

	client.Trap(["Attr.prototype.value", "Attr.prototype.nodeValue"], {
		get(ctx) {
			// eslint-disable-next-line scramjet-core/no-poisoned-ctx-value
			if (ctx.this?.ownerElement) {
				// eslint-disable-next-line scramjet-core/no-poisoned-ctx-value
				return ctx.this.ownerElement.getAttribute(ctx.this.name);
			}

			return ctx.get();
		},
		set(ctx, value) {
			// eslint-disable-next-line scramjet-core/no-poisoned-ctx-value
			if (ctx.this?.ownerElement) {
				// eslint-disable-next-line scramjet-core/no-poisoned-ctx-value
				return ctx.this.ownerElement.setAttribute(ctx.this.name, value);
			}

			return ctx.set(value);
		},
	});
}
